import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useId,
} from 'react';

import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import classNames from 'classnames';

import { useDebounce } from '../../hooks';
import { Icon } from '../../Icon';
import { Label, Preloader } from '../common';
import { useAutocompleteReducer } from '../hooks';
import { Filter } from '../types';
import { TextFieldMasked } from '../TextFieldMasked';

import useStyles from './SuggestedInput.styles';
import { SuggestedInputProps } from './SuggestedInput.types';
import { ListBox } from './ListBox';

const DEBOUNCE_TIMEOUT = 200;

/**
 * Компонент, который предоставляет поле ввода с подсказками.
 * Он поддерживает локальные и загружаемые подсказки, настраиваемое отображение вариантов и всплывающие подсказки.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param name - Атрибут name для поля ввода.
 * @param label - Текст метки для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param value - Текущее значение поля ввода.
 * @param onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param onBlur - Функция обратного вызова для обработки события потери фокуса.
 * @param placeholder - Текст плейсхолдера для поля ввода.
 * @param disabled - Если true, поле ввода будет отключено.
 * @param required - Если true, поле ввода будет отмечено как обязательное.
 * @param clearable - Если true, поле ввода будет иметь кнопку очистки.
 * @param loading - Если true, поле ввода будет показывать индикатор загрузки.
 * @param tooltip - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @param error - Если true, поле ввода будет отмечено как содержащее ошибку.
 * @param helperText - Вспомогательный текст, отображаемый с полем ввода.
 * @param renderOption - Пользовательская функция отображения вариантов.
 * @param limit - Максимальное количество элементов для запроса.
 * @param minInputValueLength - Минимальная длина ввода, необходимая для вызова подсказок.
 * @param mask - Маска, применяемая к полю ввода.
 * @param type - Тип подсказок ('local' или 'load').
 * @param loadItems - Функция для загрузки элементов на основе фильтра.
 * @param externalItems - Внешний список элементов, используемых для подсказок.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link SuggestedInputProps}
 */
export const SuggestedInput = (props: SuggestedInputProps) => {
  const {
    id,
    name,
    label,
    labelIcon,
    value,
    onChange,
    onBlur,
    placeholder,
    disabled = false,
    required = false,
    clearable = false,
    loading = false,
    tooltip,
    error = false,
    helperText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    mask,
  } = props;

  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const { items, status, hasMore, requestItems, resetItems } =
    useAutocompleteReducer<string>(
      props.type === 'load' ? props.loadItems : undefined,
    );

  const [isLoading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState(false);

  const isItemsLoading = isLoading || status === 'loading';
  const debouncedInputValue = useDebounce(value, DEBOUNCE_TIMEOUT) ?? '';

  const handleChange = useCallback(
    (_: SyntheticEvent, nextValue: string | null) => {
      onChange(nextValue ?? undefined);

      if (props.type === 'load') {
        resetItems();
        setFilter({ ...filter, offset: 0, query: '' });
      }

      setOpen(false);
    },
    [onChange, filter, props.type, resetItems],
  );

  const handleInputChange = useCallback(
    (_: SyntheticEvent, nextValue: string | null, reason: string) => {
      if (reason === 'reset') return;

      onChange(nextValue ?? undefined);

      if (nextValue?.replace(/_+/g, '').trim()) {
        if (!open) {
          setOpen(true);
        }
      } else {
        setOpen(false);
      }
    },
    [onChange, open],
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    if (open) setOpen(false);
  }, [onBlur, open]);

  const handleOpen = useCallback(() => {
    if (value?.replace(/_+/g, '').trim()) {
      setOpen(true);
    }
  }, [value]);

  const requestNextItems = () => {
    requestItems?.(filter, 'get');
    setFilter({
      ...filter,
      offset: filter.offset + filter.limit,
    });
  };

  useEffect(() => {
    if (
      props.type === 'local' ||
      !open ||
      (debouncedInputValue !== '' &&
        debouncedInputValue?.length < minInputValueLength)
    ) {
      return;
    }

    const abortRequest = requestItems?.(
      {
        ...filter,
        offset: 0,
        query: debouncedInputValue,
      },
      'search',
    );
    setFilter({
      ...filter,
      offset: filter.limit,
    });
    setLoading(false);

    return () => {
      abortRequest?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedInputValue]);

  const filterOptions = (
    optionList: string[],
    state: { inputValue: string },
  ) => {
    const cleanInput = state.inputValue.replace(/_/g, '').trim();

    return optionList.filter((optionElement: string) => {
      return optionElement.toLowerCase().includes(cleanInput.toLowerCase());
    });
  };

  return (
    <FormControl
      className={classNames(classes.suggestedInput, 'aqa_suggested_input')}
    >
      {label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
          inputId={inputId}
        />
      )}

      <Autocomplete
        classes={{
          paper: classes.paper,
          option: classes.option,
        }}
        options={props.type === 'load' ? items : props.externalItems}
        filterOptions={filterOptions}
        fullWidth
        freeSolo
        renderTags={() => null}
        disabled={disabled || loading}
        disableClearable={!clearable}
        clearIcon={
          <Icon name="dismiss_substract" color="stroke.secondary" size="24" />
        }
        inputValue={loading ? 'Загрузка...' : value}
        onInputChange={mask ? undefined : handleInputChange}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleBlur}
        renderOption={(renderOptionProps, option) => (
          <li {...renderOptionProps}>
            {renderOption ? renderOption(option) : <div>{option}</div>}
          </li>
        )}
        slotProps={{
          listbox: {
            component: ListBox,
            ...{
              dataLength: items.length,
              next: requestNextItems,
              hasMore,
              loading: isItemsLoading,
              long: !!renderOption,
            },
          },
        }}
        renderInput={(params) => {
          return mask ? (
            <TextFieldMasked
              {...params}
              id={inputId}
              mask={mask}
              name={name}
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              loading={loading}
              classes={{ root: classes.inputRoot }}
              onChange={handleInputChange as never}
            />
          ) : (
            <TextField
              {...params}
              id={inputId}
              name={name}
              classes={{ root: classes.inputRoot }}
              autoComplete="off"
              variant="outlined"
              fullWidth
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              {...(loading
                ? {
                    slotProps: {
                      input: {
                        endAdornment: <Preloader />,
                      },
                    },
                  }
                : {})}
            />
          );
        }}
      />
    </FormControl>
  );
};
